import { useI18n } from "@/hooks/useI18n";
import { dictionary } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { ArrowRight, ChevronRight } from "lucide-react";

const handleAnchor = (id) => (event) => {
  event.preventDefault();
  const node = document.getElementById(id);
  if (node) node.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Hero = () => {
  const { lang } = useI18n();
  const data = dictionary.hero;
  const about = dictionary.about;

  return (
    <section
      id="about"
      data-testid="hero-section"
      className="relative pt-36 pb-20 lg:pt-44 lg:pb-28 overflow-hidden cads-hero-gradient border-b border-[#E2E8F0]"
    >
      <div className="absolute inset-0 cads-grid opacity-60 [mask-image:radial-gradient(ellipse_60%_60%_at_50%_0%,#000_60%,transparent_100%)] pointer-events-none" />

      <div className="relative container mx-auto px-6 md:px-10 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-7">
            <div
              data-testid="hero-badge"
              className="inline-flex items-center gap-2 mb-7 px-3 py-1.5 border border-[#0F2756]/15 bg-white/70 backdrop-blur"
            >
              <span className="h-1.5 w-1.5 bg-[#1857D6] animate-pulse" />
              <span className="text-[11px] font-semibold tracking-[0.25em] text-[#0F2756]">
                {data.badge[lang]}
              </span>
            </div>

            <h1
              data-testid="hero-title"
              className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black tracking-tighter leading-[1.04] text-[#0A1933]"
              style={{ fontFamily: "Manrope, IBM Plex Sans, sans-serif" }}
            >
              {data.title_top[lang]}
              <br />
              <span className="bg-gradient-to-r from-[#1857D6] via-[#1E90FF] to-[#4FC3F7] bg-clip-text text-transparent">
                {data.title_bottom[lang]}
              </span>
            </h1>

            <p
              data-testid="hero-subtitle"
              className="mt-7 text-base sm:text-lg text-[#475569] leading-relaxed max-w-2xl"
            >
              {data.subtitle[lang]}
            </p>

            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Button
                data-testid="hero-primary-cta"
                onClick={handleAnchor("form")}
                className="group bg-[#0F2756] hover:bg-[#1857D6] text-white rounded-none h-13 px-7 font-semibold tracking-wide text-base"
              >
                {data.primary_cta[lang]}
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                data-testid="hero-secondary-cta"
                variant="outline"
                onClick={handleAnchor("products")}
                className="group bg-white border-[#0F2756]/20 hover:border-[#0F2756] hover:bg-[#0F2756] hover:text-white text-[#0F2756] rounded-none h-13 px-7 font-semibold tracking-wide text-base"
              >
                {data.secondary_cta[lang]}
                <ChevronRight className="ml-1 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative bg-white border border-[#E2E8F0] p-7 lg:p-9 shadow-[0_20px_60px_-20px_rgba(15,39,86,0.18)]">
              <div className="absolute -top-px left-7 right-7 h-px bg-gradient-to-r from-transparent via-[#1857D6] to-transparent" />
              <span className="text-[10px] font-semibold tracking-[0.3em] text-[#1857D6] uppercase">
                ▍ {about.label[lang]}
              </span>
              <h2
                className="mt-3 text-2xl lg:text-3xl font-bold tracking-tight text-[#0A1933] leading-tight"
                style={{ fontFamily: "Manrope, IBM Plex Sans, sans-serif" }}
              >
                {about.title[lang]}
              </h2>
              <div className="mt-5 space-y-3 text-[14px] lg:text-[15px] text-[#475569] leading-relaxed">
                {about.paragraphs[lang].map((p, idx) => (
                  <p key={idx} data-testid={`about-paragraph-${idx}`}>
                    {p}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 border-y border-[#E2E8F0]">
          {data.stats.map((stat) => (
            <div
              key={stat.value}
              data-testid={`hero-stat-${stat.value}`}
              className="py-6 px-2 sm:px-6 border-l first:border-l-0 border-[#E2E8F0] flex items-baseline gap-5"
            >
              <div
                className="text-3xl sm:text-4xl font-black text-[#0F2756] tracking-tight"
                style={{ fontFamily: "Manrope, IBM Plex Sans, sans-serif" }}
              >
                {stat.value}
              </div>
              <div className="text-[11px] tracking-[0.18em] text-[#475569] uppercase leading-tight">
                {stat.label[lang]}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
